import React, { useState, useEffect, useContext, useMemo } from "react";
import {
    makeStyles,
    Paper,
    Grid,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    IconButton,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    FormControlLabel,
    Checkbox,
    Tooltip,
    Box,
    Button,
    Typography
} from "@material-ui/core";
import { Formik, Form, Field } from 'formik';
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import { Edit as EditIcon, Help as HelpIcon } from "@material-ui/icons";

import { toast } from "react-toastify";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";
import api from "../../services/api";
import Conecthub360Modal from "../Conecthub360Modal";


const useStyles = makeStyles(theme => ({
    root: {
        width: '100%'
    },
    mainPaper: {
        width: '100%',
        flex: 1,
        padding: theme.spacing(2)
    },
    fullWidth: {
        width: '100%'
    },
    tableContainer: {
        width: '100%',
        overflowX: "scroll",
        ...theme.scrollbarStyles
    },
    textfield: {
        width: '100%'
    },
    textRight: {
        textAlign: 'right'
    },
    row: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2)
    },
    control: {
        paddingRight: theme.spacing(1),
        paddingLeft: theme.spacing(1)
    },
    buttonContainer: {
        textAlign: 'right',
        padding: theme.spacing(1)
    },
    conecthubGlobalRow: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1)
    },
    /** Bloco compacto: não estica na largura total da página */
    conecthubGlobalWrap: {
        maxWidth: 640,
        width: "100%",
        [theme.breakpoints.down("xs")]: {
            maxWidth: "100%"
        }
    },
    conecthubLinkField: {
        flex: "0 1 360px",
        minWidth: 180,
        maxWidth: 400,
        width: "100%"
    },
    /** Botões e campo na mesma linha, centrados no eixo vertical */
    conecthubToolbar: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: theme.spacing(1.25)
    }
}));

export function PlanManagerForm(props) {
    const {
        onSubmit,
        onDelete,
        onCancel,
        initialValue,
        loading
    } = props;
    const classes = useStyles()

    const [record, setRecord] = useState({
        name: '',
        users: 0,
        connections: 0,
        queues: 0,
        amount: 0,
        useWhatsapp: true,
        useFacebook: true,
        useInstagram: true,
        useCampaigns: true,
        useSchedules: true,
        useInternalChat: true,
        useExternalApi: true,
        useKanban: true,
        useKanbanEnterprise: false,
        useOpenAi: true,
        useIntegrations: true,
        isPublic: true,
        useWhatsappOfficial: false,
        useWhatsMeow: true,
        trial: false,
        trialDays: 3,
        recurrence: 'monthly',
        wavoip: false,
        useConecthub360: false,
        useGroupManagement: false,
        usePhraseListFlow: true,
        useConversationFlow: true,
        useFollowUp: true,
        diskQuota: 2, // Padrão: 2 GB
        checkDiskQuota: false // Padrão: desativado (não verifica quota antes de upload)
    });

    useEffect(() => {
        setRecord(initialValue)
    }, [initialValue])

    const handleSubmit = async (data) => {
        onSubmit(data)
    }

    return (
        <Formik
            enableReinitialize
            className={classes.fullWidth}
            initialValues={record}
            onSubmit={(values, { resetForm }) =>
                setTimeout(() => {
                    handleSubmit(values)
                    resetForm()
                }, 500)
            }
        >
            {(values) => (
                <Form className={classes.fullWidth}>
                    <Grid spacing={1} justifyContent="flex-start" container>

                        {/* PUBLICO */}
                        <Grid xs={12} sm={6} md={1} xl={1} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="status-selection">{i18n.t("plans.form.public")}</InputLabel>
                                <Field
                                    as={Select}
                                    id="status-selection"
                                    label={i18n.t("plans.form.public")}
                                    labelId="status-selection-label"
                                    name="isPublic"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>Sim</MenuItem>
                                    <MenuItem value={false}>Não</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* TRIAL */}
                        <Grid xs={12} sm={6} md={1} xl={1} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="trial-selection">Trial</InputLabel>
                                <Field
                                    as={Select}
                                    id="trial-selection"
                                    label="Trial"
                                    labelId="trial-selection-label"
                                    name="trial"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>Sim</MenuItem>
                                    <MenuItem value={false}>Não</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* TRIAL DAYS */}
                        <Grid xs={12} sm={6} md={1} xl={1} item>
                            <Field
                                as={TextField}
                                label={i18n.t("plans.form.trialDays")}
                                name="trialDays"
                                variant="outlined"
                                className={classes.fullWidth}
                                margin="dense"
                                type="number"
                                inputProps={{ min: 1 }}
                                disabled={!values.values.trial}
                            />
                        </Grid>

                        {/* NOME */}
                        <Grid xs={12} sm={6} md={3} xl={3} item>
                            <Field
                                as={TextField}
                                label={i18n.t("plans.form.name")}
                                name="name"
                                variant="outlined"
                                className={classes.fullWidth}
                                margin="dense"
                            />
                        </Grid>

                        {/* USUARIOS */}
                        <Grid xs={12} sm={6} md={1} item>
                            <Field
                                as={TextField}
                                label={i18n.t("plans.form.users")}
                                name="users"
                                variant="outlined"
                                className={classes.fullWidth}
                                margin="dense"
                                type="number"
                            />
                        </Grid>

                        {/* CONEXOES */}
                        <Grid xs={12} sm={6} md={1} item>
                            <Field
                                as={TextField}
                                label={i18n.t("plans.form.connections")}
                                name="connections"
                                variant="outlined"
                                className={classes.fullWidth}
                                margin="dense"
                                type="number"
                            />
                        </Grid>

                        {/* FILAS */}
                        <Grid xs={12} sm={6} md={1} item>
                            <Field
                                as={TextField}
                                label="Filas"
                                name="queues"
                                variant="outlined"
                                className={classes.fullWidth}
                                margin="dense"
                                type="number"
                            />
                        </Grid>

                        {/* VALOR */}
                        <Grid xs={12} sm={6} md={1} item>
                            <Field
                                as={TextField}
                                label="Valor"
                                name="amount"
                                variant="outlined"
                                className={classes.fullWidth}
                                margin="dense"
                                type="text"
                            />
                        </Grid>

                        {/* WHATSAPP */}
                        <Grid xs={12} sm={6} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useWhatsapp-selection">Whatsapp</InputLabel>
                                <Field
                                    as={Select}
                                    id="useWhatsapp-selection"
                                    label="Whatsapp"
                                    labelId="useWhatsapp-selection-label"
                                    name="useWhatsapp"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* WHATSAPP OFICIAL */}
                        <Grid xs={12} sm={6} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useWhatsappOfficial-selection">Whatsapp Oficial</InputLabel>
                                <Field
                                    as={Select}
                                    id="useWhatsappOfficial-selection"
                                    label="Whatsapp Oficial"
                                    labelId="useWhatsappOfficial-selection-label"
                                    name="useWhatsappOfficial"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* WHATSMEOW (WuzAPI / Meow) */}
                        <Grid xs={12} sm={6} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useWhatsMeow-selection">{i18n.t("plans.form.whatsappMeow")}</InputLabel>
                                <Field
                                    as={Select}
                                    id="useWhatsMeow-selection"
                                    label={i18n.t("plans.form.whatsappMeow")}
                                    labelId="useWhatsMeow-selection-label"
                                    name="useWhatsMeow"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* FACEBOOK */}
                        <Grid xs={12} sm={6} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useFacebook-selection">Facebook</InputLabel>
                                <Field
                                    as={Select}
                                    id="useFacebook-selection"
                                    label="Facebook"
                                    labelId="useFacebook-selection-label"
                                    name="useFacebook"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* INSTAGRAM */}
                        <Grid xs={12} sm={6} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useInstagram-selection">Instagram</InputLabel>
                                <Field
                                    as={Select}
                                    id="useInstagram-selection"
                                    label="Instagram"
                                    labelId="useInstagram-selection-label"
                                    name="useInstagram"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* CAMPANHAS */}
                        <Grid xs={12} sm={6} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useCampaigns-selection">{i18n.t("plans.form.campaigns")}</InputLabel>
                                <Field
                                    as={Select}
                                    id="useCampaigns-selection"
                                    label={i18n.t("plans.form.campaigns")}
                                    labelId="useCampaigns-selection-label"
                                    name="useCampaigns"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* AGENDAMENTOS */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useSchedules-selection">{i18n.t("plans.form.schedules")}</InputLabel>
                                <Field
                                    as={Select}
                                    id="useSchedules-selection"
                                    label={i18n.t("plans.form.schedules")}
                                    labelId="useSchedules-selection-label"
                                    name="useSchedules"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* CHAT INTERNO */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useInternalChat-selection">Chat Interno</InputLabel>
                                <Field
                                    as={Select}
                                    id="useInternalChat-selection"
                                    label="Chat Interno"
                                    labelId="useInternalChat-selection-label"
                                    name="useInternalChat"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* API Externa */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useExternalApi-selection">API Externa</InputLabel>
                                <Field
                                    as={Select}
                                    id="useExternalApi-selection"
                                    label="API Externa"
                                    labelId="useExternalApi-selection-label"
                                    name="useExternalApi"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* KANBAN */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useKanban-selection">Kanban</InputLabel>
                                <Field
                                    as={Select}
                                    id="useKanban-selection"
                                    label="Kanban"
                                    labelId="useKanban-selection-label"
                                    name="useKanban"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* KANBAN ENTERPRISE */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useKanbanEnterprise-selection">Kanban Enterprise</InputLabel>
                                <Field
                                    as={Select}
                                    id="useKanbanEnterprise-selection"
                                    label="Kanban Enterprise"
                                    labelId="useKanbanEnterprise-selection-label"
                                    name="useKanbanEnterprise"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* OPENAI */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useOpenAi-selection">Talk.Ai</InputLabel>
                                <Field
                                    as={Select}
                                    id="useOpenAi-selection"
                                    label="Talk.Ai"
                                    labelId="useOpenAi-selection-label"
                                    name="useOpenAi"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* INTEGRACOES */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useIntegrations-selection">Integrações</InputLabel>
                                <Field
                                    as={Select}
                                    id="useIntegrations-selection"
                                    label="Integrações"
                                    labelId="useIntegrations-selection-label"
                                    name="useIntegrations"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* CONECTHUB 360 */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useConecthub360-selection">{i18n.t("plans.form.conecthub360")}</InputLabel>
                                <Field
                                    as={Select}
                                    id="useConecthub360-selection"
                                    label={i18n.t("plans.form.conecthub360")}
                                    labelId="useConecthub360-selection-label"
                                    name="useConecthub360"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* WAVOIP */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="wavoip-selection">Wavoip</InputLabel>
                                <Field
                                    as={Select}
                                    id="wavoip-selection"
                                    label="Wavoip"
                                    labelId="wavoip-selection-label"
                                    name="wavoip"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* GESTÃO DE GRUPOS */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useGroupManagement-selection">{i18n.t("plans.form.groupManagement")}</InputLabel>
                                <Field
                                    as={Select}
                                    id="useGroupManagement-selection"
                                    label={i18n.t("plans.form.groupManagement")}
                                    labelId="useGroupManagement-selection-label"
                                    name="useGroupManagement"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* AUTOMAÇÕES (menu Automações) */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="usePhraseListFlow-selection">{i18n.t("plans.form.phraseListFlow")}</InputLabel>
                                <Field
                                    as={Select}
                                    id="usePhraseListFlow-selection"
                                    label={i18n.t("plans.form.phraseListFlow")}
                                    labelId="usePhraseListFlow-selection-label"
                                    name="usePhraseListFlow"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useConversationFlow-selection">{i18n.t("plans.form.conversationFlow")}</InputLabel>
                                <Field
                                    as={Select}
                                    id="useConversationFlow-selection"
                                    label={i18n.t("plans.form.conversationFlow")}
                                    labelId="useConversationFlow-selection-label"
                                    name="useConversationFlow"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth>
                                <InputLabel htmlFor="useFollowUp-selection">{i18n.t("plans.form.followUpFlow")}</InputLabel>
                                <Field
                                    as={Select}
                                    id="useFollowUp-selection"
                                    label={i18n.t("plans.form.followUpFlow")}
                                    labelId="useFollowUp-selection-label"
                                    name="useFollowUp"
                                    margin="dense"
                                >
                                    <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* DISCO (GB) */}
                        <Grid xs={12} sm={8} md={2} item>
                            <Field
                                as={TextField}
                                label="Disco (GB)"
                                name="diskQuota"
                                variant="outlined"
                                className={classes.fullWidth}
                                margin="dense"
                                type="number"
                                inputProps={{ min: 0, step: 0.1 }}
                                helperText="Quota de disco em GB (padrão: 2 GB)"
                            />
                        </Grid>

                        {/* BLOQUEIO DE MÍDIA (Verificação de Quota) */}
                        <Grid xs={12} sm={8} md={2} item>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <FormControlLabel
                                    control={
                                        <Field
                                            name="checkDiskQuota"
                                            type="checkbox"
                                            component={({ field }) => (
                                                <Checkbox
                                                    {...field}
                                                    checked={field.value === true}
                                                    color="primary"
                                                />
                                            )}
                                        />
                                    }
                                    label="Bloqueio de Mídia"
                                />
                                <Tooltip
                                    title={
                                        <div style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                                            <strong>Atenção:</strong> Ativar esta opção pode deixar o sistema mais lento, 
                                            necessitando de um servidor melhor. O sistema vai validar cada mídia enviada 
                                            para verificar se a empresa está com quota disponível antes de permitir o upload.
                                        </div>
                                    }
                                    arrow
                                    placement="top"
                                >
                                    <IconButton
                                        size="small"
                                        style={{ padding: '4px', color: '#666' }}
                                        aria-label="Ajuda sobre Bloqueio de Mídia"
                                    >
                                        <HelpIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '4px' }}>
                                Ativar verificação de quota antes de uploads
                            </div>
                        </Grid>

                    </Grid>
                    <Grid spacing={2} justifyContent="flex-end" container>

                        <Grid sm={3} md={2} item>
                            <ButtonWithSpinner className={classes.fullWidth} loading={loading} onClick={() => onCancel()} variant="contained">
                                {i18n.t("plans.form.clear")}
                            </ButtonWithSpinner>
                        </Grid>
                        {record.id !== undefined ? (
                            <Grid sm={3} md={2} item>
                                <ButtonWithSpinner className={classes.fullWidth} loading={loading} onClick={() => onDelete(record)} variant="contained" color="secondary">
                                    {i18n.t("plans.form.delete")}
                                </ButtonWithSpinner>
                            </Grid>
                        ) : null}
                        <Grid sm={3} md={2} item>
                            <ButtonWithSpinner className={classes.fullWidth} loading={loading} type="submit" variant="contained" color="primary">
                                {i18n.t("plans.form.save")}
                            </ButtonWithSpinner>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
    )
}

export function PlansManagerGrid(props) {
    const { records, onSelect } = props
    const classes = useStyles()

    const isDedicatedPlanRow = (row) =>
        row.dedicatedCompanyId != null && Number(row.dedicatedCompanyId) > 0

    const renderWhatsapp = (row) => {
        return row.useWhatsapp === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderWhatsappOfficial = (row) => {
        return row.useWhatsappOfficial === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderWhatsMeow = (row) => {
        return row.useWhatsMeow === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderFacebook = (row) => {
        return row.useFacebook === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderInstagram = (row) => {
        return row.useInstagram === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderCampaigns = (row) => {
        return row.useCampaigns === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderSchedules = (row) => {
        return row.useSchedules === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderInternalChat = (row) => {
        return row.useInternalChat === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderExternalApi = (row) => {
        return row.useExternalApi === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderKanban = (row) => {
        return row.useKanban === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderKanbanEnterprise = (row) => {
        return row.useKanbanEnterprise === true ? `${i18n.t("plans.form.yes")}` : `${i18n.t("plans.form.no")}`;
    };

    const renderOpenAi = (row) => {
        return row.useOpenAi === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderIntegrations = (row) => {
        return row.useIntegrations === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderWavoip = (row) => {
        return row.wavoip === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderConecthub360 = (row) => {
        return row.useConecthub360 === true ? `${i18n.t("plans.form.yes")}` : `${i18n.t("plans.form.no")}`;
    };

    const renderPhraseListFlow = (row) =>
        row.usePhraseListFlow === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    const renderConversationFlow = (row) =>
        row.useConversationFlow === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    const renderFollowUp = (row) =>
        row.useFollowUp === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;

    return (
        <Paper className={classes.tableContainer}>
            <Table
                className={classes.fullWidth}
                // size="small"
                padding="none"
                aria-label="a dense table"
            >
                <TableHead>
                    <TableRow>
                        <TableCell align="center" style={{ width: '1%' }}>#</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.trial")}</TableCell>
                        <TableCell align="left">{i18n.t("plans.form.name")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.users")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.public")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.connections")}</TableCell>
                        <TableCell align="center">Filas</TableCell>
                        <TableCell align="center">Valor</TableCell>
                        <TableCell align="center">Whatsapp</TableCell>
                        <TableCell align="center">Whatsapp Oficial</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.whatsappMeow")}</TableCell>
                        <TableCell align="center">Facebook</TableCell>
                        <TableCell align="center">Instagram</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.campaigns")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.schedules")}</TableCell>
                        <TableCell align="center">Chat Interno</TableCell>
                        <TableCell align="center">API Externa</TableCell>
                        <TableCell align="center">Kanban</TableCell>
                        <TableCell align="center">Kanban Ent.</TableCell>
                        <TableCell align="center">Talk.Ai</TableCell>
                        <TableCell align="center">Integrações</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.conecthub360")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.phraseListFlowShort")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.conversationFlowShort")}</TableCell>
                        <TableCell align="center">{i18n.t("plans.form.followUpFlowShort")}</TableCell>
                        <TableCell align="center">Wavoip</TableCell>
                        <TableCell align="center">Disco (GB)</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {records.map((row) => {
                        const dedicated = isDedicatedPlanRow(row)
                        return (
                        <TableRow
                            key={row.id}
                            style={dedicated ? { backgroundColor: "rgba(103, 58, 183, 0.06)" } : undefined}
                        >
                            <TableCell align="center" style={{ width: '1%' }}>
                                <Tooltip
                                    title={
                                        dedicated
                                            ? "Plano dedicado: edição apenas em Configurações → Empresas"
                                            : "Editar plano"
                                    }
                                >
                                    <span>
                                        <IconButton
                                            disabled={dedicated}
                                            onClick={() => onSelect(row)}
                                            aria-label="edit-plan"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="center">{row.trialDays || '-'}</TableCell>
                            <TableCell align="left">
                                {row.name || '-'}
                                {dedicated ? (
                                    <Typography component="span" variant="caption" style={{ display: "block", color: "#5e35b1" }}>
                                        Empresa ID {row.dedicatedCompanyId} · dedicado
                                    </Typography>
                                ) : null}
                            </TableCell>
                            <TableCell align="center">{row.users || '-'}</TableCell>
                            <TableCell align="center">{row.isPublic ? "Sim" : "Não" || '-'}</TableCell>
                            <TableCell align="center">{row.connections || '-'}</TableCell>
                            <TableCell align="center">{row.queues || '-'}</TableCell>
                            <TableCell align="center">{i18n.t("plans.form.money")} {row.amount ? row.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 }) : '00.00'}</TableCell>
                            <TableCell align="center">{renderWhatsapp(row)}</TableCell>
                            <TableCell align="center">{renderWhatsappOfficial(row)}</TableCell>
                            <TableCell align="center">{renderWhatsMeow(row)}</TableCell>
                            <TableCell align="center">{renderFacebook(row)}</TableCell>
                            <TableCell align="center">{renderInstagram(row)}</TableCell>
                            <TableCell align="center">{renderCampaigns(row)}</TableCell>
                            <TableCell align="center">{renderSchedules(row)}</TableCell>
                            <TableCell align="center">{renderInternalChat(row)}</TableCell>
                            <TableCell align="center">{renderExternalApi(row)}</TableCell>
                            <TableCell align="center">{renderKanban(row)}</TableCell>
                            <TableCell align="center">{renderKanbanEnterprise(row)}</TableCell>
                            <TableCell align="center">{renderOpenAi(row)}</TableCell>
                            <TableCell align="center">{renderIntegrations(row)}</TableCell>
                            <TableCell align="center">{renderConecthub360(row)}</TableCell>
                            <TableCell align="center">{renderPhraseListFlow(row)}</TableCell>
                            <TableCell align="center">{renderConversationFlow(row)}</TableCell>
                            <TableCell align="center">{renderFollowUp(row)}</TableCell>
                            <TableCell align="center">{renderWavoip(row)}</TableCell>
                            <TableCell align="center">
                                {row.diskQuota ? (row.diskQuota / (1024 * 1024 * 1024)).toFixed(2) : '2.00'} GB
                            </TableCell>
                        </TableRow>
                    )})}
                </TableBody>
            </Table>
        </Paper>
    )
}

export default function PlansManager() {
    const classes = useStyles()
    const { list, save, update, remove } = usePlans()
    const { user } = useContext(AuthContext)
    const { find } = useCompanies()

    const [masterConecthubPublicUrl, setMasterConecthubPublicUrl] = useState("")
    const [masterConecthubAccountId, setMasterConecthubAccountId] = useState("")
    const [masterConecthubMessagingApiKey, setMasterConecthubMessagingApiKey] = useState("")
    const [conecthubModalOpen, setConecthubModalOpen] = useState(false)

    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [loading, setLoading] = useState(false)
    const [records, setRecords] = useState([])
    const [record, setRecord] = useState({
        name: '',
        users: 0,
        connections: 0,
        queues: 0,
        amount: 0,
        useWhatsapp: true,
        useFacebook: true,
        useInstagram: true,
        useCampaigns: true,
        useSchedules: true,
        useInternalChat: true,
        useExternalApi: true,
        useKanban: true,
        useKanbanEnterprise: false,
        useOpenAi: true,
        useIntegrations: true,
        isPublic: true,
        useWhatsappOfficial: false,
        useWhatsMeow: true,
        trial: false,
        trialDays: 3,
        recurrence: 'monthly',
        wavoip: false,
        useConecthub360: false,
        useGroupManagement: false,
        usePhraseListFlow: true,
        useConversationFlow: true,
        useFollowUp: true,
        diskQuota: 2, // Padrão: 2 GB
        checkDiskQuota: false // Padrão: desativado (não verifica quota antes de upload)
    })

    useEffect(() => {
        async function fetchData() {
            await loadPlans()
        }
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record, user?.super])

    useEffect(() => {
        if (!user?.super) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const company = await find(1);
                if (!cancelled) {
                    setMasterConecthubPublicUrl(company?.conecthub360PublicUrl ?? "");
                    setMasterConecthubAccountId(company?.conecthub360AccountId ?? "");
                    setMasterConecthubMessagingApiKey(
                        company?.conecthub360MessagingApiKey ?? ""
                    );
                }
            } catch (e) {
                /* silencioso: master sem acesso ou empresa 1 inexistente */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [user?.super])

    const handleSaveMasterConecthubPublicUrl = async () => {
        try {
            await api.put("/companies/1/conecthub360-public-url", {
                conecthub360PublicUrl: masterConecthubPublicUrl,
                conecthub360AccountId: masterConecthubAccountId,
                conecthub360MessagingApiKey: masterConecthubMessagingApiKey
            });
            toast.success(i18n.t("plans.form.conecthubPublicLinkToastOk"));
        } catch (e) {
            toast.error(i18n.t("plans.form.conecthubPublicLinkToastErr"));
        }
    }

    const loadPlans = async () => {
        setLoading(true)
        try {
            const planList = await list(
                user?.super ? { includeDedicated: "true" } : undefined
            )
            setRecords(planList)
        } catch (e) {
            toast.error('Não foi possível carregar a lista de registros')
        }
        setLoading(false)
    }

    const handleSubmit = async (data) => {
        setLoading(true)
        // Converter GB para bytes antes de enviar
        const planData = {
            ...data,
            diskQuota: data.diskQuota ? Math.round(data.diskQuota * 1024 * 1024 * 1024) : 2147483648 // 2 GB padrão em bytes
        }
        console.log(planData)
        try {
            if (planData.id !== undefined) {
                await update(planData)
            } else {
                await save(planData)
            }
            await loadPlans()
            handleCancel()
            toast.success('Operação realizada com sucesso!')
        } catch (e) {
            toast.error('Não foi possível realizar a operação. Verifique se já existe uma plano com o mesmo nome ou se os campos foram preenchidos corretamente')
        }
        setLoading(false)
    }

    const handleDelete = async () => {
        setLoading(true)
        try {
            await remove(record.id)
            await loadPlans()
            handleCancel()
            toast.success('Operação realizada com sucesso!')
        } catch (e) {
            toast.error('Não foi possível realizar a operação')
        }
        setLoading(false)
    }

    const handleOpenDeleteDialog = () => {
        setShowConfirmDialog(true)
    }

    const handleCancel = () => {
        setRecord({
            id: undefined,
            name: '',
            users: 0,
            connections: 0,
            queues: 0,
            amount: 0,
            useWhatsapp: true,
            useFacebook: true,
            useInstagram: true,
            useCampaigns: true,
            useSchedules: true,
            useInternalChat: true,
            useExternalApi: true,
            useKanban: true,
            useKanbanEnterprise: false,
            useOpenAi: true,
            useIntegrations: true,
            isPublic: true,
            useWhatsappOfficial: false,
            useWhatsMeow: true,
            trial: false,
            trialDays: 3,
            recurrence: 'monthly',
            wavoip: false,
            useConecthub360: false,
            useGroupManagement: false,
            usePhraseListFlow: true,
            useConversationFlow: true,
            useFollowUp: true,
            diskQuota: 2, // Padrão: 2 GB
            checkDiskQuota: false // Padrão: desativado
        })
    }

    const handleSelect = (data) => {
        const dedicatedId = data.dedicatedCompanyId
        if (dedicatedId != null && Number(dedicatedId) > 0) {
            toast.info(
                "Plano dedicado a uma empresa (PlanEmpresa): altere módulos e limites em Configurações → Empresas, não nesta aba."
            )
            return
        }

        let useWhatsapp = data.useWhatsapp === false ? false : true
        let useWhatsappOfficial = data.useWhatsappOfficial === false ? false : true
        let useWhatsMeow = data.useWhatsMeow === false ? false : true
        let useFacebook = data.useFacebook === false ? false : true
        let useInstagram = data.useInstagram === false ? false : true
        let useCampaigns = data.useCampaigns === false ? false : true
        let useSchedules = data.useSchedules === false ? false : true
        let useInternalChat = data.useInternalChat === false ? false : true
        let useExternalApi = data.useExternalApi === false ? false : true
        let useKanban = data.useKanban === false ? false : true
        let useKanbanEnterprise = data.useKanbanEnterprise === true ? true : false
        let useOpenAi = data.useOpenAi === false ? false : true
        let useIntegrations = data.useIntegrations === false ? false : true
        let wavoip = data.wavoip === false ? false : true
        let useConecthub360 = data.useConecthub360 === true ? true : false
        let useGroupManagement = data.useGroupManagement === false ? false : true
        let usePhraseListFlow = data.usePhraseListFlow === false ? false : true
        let useConversationFlow = data.useConversationFlow === false ? false : true
        let useFollowUp = data.useFollowUp === false ? false : true

        // Converter bytes para GB para exibição
        const diskQuotaGB = data.diskQuota ? (data.diskQuota / (1024 * 1024 * 1024)) : 2
        // checkDiskQuota vem do banco (padrão: false se não existir)
        const checkDiskQuota = data.checkDiskQuota === true ? true : false

        setRecord({
            id: data.id,
            name: data.name || '',
            users: data.users || 0,
            connections: data.connections || 0,
            queues: data.queues || 0,
            amount: data.amount?.toLocaleString('pt-br', { minimumFractionDigits: 2 }) || 0,
            useWhatsapp,
            useWhatsappOfficial,
            useWhatsMeow,
            useFacebook,
            useInstagram,
            useCampaigns,
            useSchedules,
            useInternalChat,
            useExternalApi,
            useKanban,
            useKanbanEnterprise,
            useOpenAi,
            useIntegrations,
            isPublic: data.isPublic,
            trial: data.trial,
            trialDays: data.trialDays,
            recurrence: data.recurrence,
            wavoip,
            useConecthub360,
            useGroupManagement,
            usePhraseListFlow,
            useConversationFlow,
            useFollowUp,
            diskQuota: diskQuotaGB,
            checkDiskQuota: checkDiskQuota
        })
    }

    const hasAnyPlanWithConecthub360 = useMemo(
        () => records.some((p) => p.useConecthub360 === true),
        [records]
    );

    const showConecthubGlobalTools = !!user?.super && hasAnyPlanWithConecthub360;

    return (
        <Paper className={classes.mainPaper} elevation={0}>
            <Grid spacing={2} container>
                <Grid xs={12} item>
                    <PlanManagerForm
                        initialValue={record}
                        onDelete={handleOpenDeleteDialog}
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                        loading={loading}
                    />
                </Grid>
                {showConecthubGlobalTools ? (
                    <Grid xs={12} item className={classes.conecthubGlobalRow}>
                        <Box className={classes.conecthubGlobalWrap}>
                        <Box className={classes.conecthubToolbar}>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => setConecthubModalOpen(true)}
                            >
                                {i18n.t("plans.form.conecthubOpen")}
                            </Button>
                            <TextField
                                label={i18n.t("plans.form.conecthubPublicLink")}
                                variant="outlined"
                                color="primary"
                                className={classes.conecthubLinkField}
                                margin="none"
                                size="small"
                                value={masterConecthubPublicUrl}
                                onChange={(e) => setMasterConecthubPublicUrl(e.target.value)}
                                placeholder="https://..."
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={handleSaveMasterConecthubPublicUrl}
                            >
                                {i18n.t("plans.form.conecthubPublicLinkSave")}
                            </Button>
                        </Box>
                        <Box mt={1}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label={i18n.t("plans.form.conecthubAccountId")}
                                        variant="outlined"
                                        color="primary"
                                        margin="none"
                                        size="small"
                                        fullWidth
                                        value={masterConecthubAccountId}
                                        onChange={(e) => setMasterConecthubAccountId(e.target.value)}
                                        placeholder="UUID do ConectHUB"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label={i18n.t("plans.form.conecthubMessagingApiKey")}
                                        variant="outlined"
                                        color="primary"
                                        margin="none"
                                        size="small"
                                        fullWidth
                                        value={masterConecthubMessagingApiKey}
                                        onChange={(e) =>
                                            setMasterConecthubMessagingApiKey(e.target.value)
                                        }
                                        placeholder="sk_live_..."
                                        helperText={i18n.t("plans.form.conecthubMessagingApiKeyHint")}
                                        autoComplete="off"
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        </Box>
                        <Conecthub360Modal
                            open={conecthubModalOpen}
                            onClose={() => setConecthubModalOpen(false)}
                        />
                    </Grid>
                ) : null}
                <Grid xs={12} item>
                    <PlansManagerGrid
                        records={records}
                        onSelect={handleSelect}
                    />
                </Grid>
            </Grid>
            <ConfirmationModal
                title="Exclusão de Registro"
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={() => handleDelete()}
            >
                Deseja realmente excluir esse registro?
            </ConfirmationModal>
        </Paper>
    )
}